import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import StepFlow, { StepFlowStep } from '../../components/misc/StepFlow';
import Input from '../../components/misc/Input';
import { COMMUNITY_URL } from '../../../constants';
import Slider from '../../components/misc/Slider';
import photoAlbumService from '../../../services/photoAlbumService';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';

interface ViewProps {
  setStep: (step: 1 | -1) => void;
  album: CreatePhotoAlbum;
  setAlbum: (album: CreatePhotoAlbum) => void;
}

interface AlbumTitleViewProps extends ViewProps {
  coverPhotoPreview: string | undefined;
  setCoverPhotoPreview: (preview: string | undefined) => void;
}

const formatTitleToId = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove non-alphanumeric characters except spaces
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

export default function CreatePhotoAlbumPage() {
  const [step, setStep] = useState<number>(0);
  const business = useSelectedBusiness();

  const [album, setAlbum] = useState<CreatePhotoAlbum>({
    id: '',
    title: '',
    description: '',
    coverPhoto: undefined,
    businessId: business?.businessId || 0,
    openToUpload: true,
  });
  const [coverPhotoPreview, setCoverPhotoPreview] = useState<string | undefined>(undefined);

  const handleStep = (diff: 1 | -1) => setStep((prev) => prev + diff);

  const props: ViewProps = { setStep: handleStep, album, setAlbum };

  const steps = [
    <AlbumTitleView 
      setStep={handleStep} 
      album={album} 
      setAlbum={setAlbum} 
      coverPhotoPreview={coverPhotoPreview} 
      setCoverPhotoPreview={setCoverPhotoPreview} 
    />,
    <AlbumDetailsView {...props} />,
    <UploadSettingsView {...props} />,
  ];

  return <StepFlow indexState={[step, setStep]} steps={steps} />;
}

function AlbumTitleView({ setStep, album, setAlbum, coverPhotoPreview, setCoverPhotoPreview }: AlbumTitleViewProps) {
  const { t } = useTranslation();

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const { resizeImage } = await import('../../../utils/formatUtils');
      const resizedFile = await resizeImage(file, 768);
      
      setAlbum({ ...album, coverPhoto: resizedFile });
      setCoverPhotoPreview(URL.createObjectURL(resizedFile));
    } catch (error) {
      console.error('Error processing image:', error);
    }
  };

  const handleRemoveImage = () => {
    setAlbum({ ...album, coverPhoto: undefined });
    setCoverPhotoPreview(undefined);
  };

  return (
    <StepFlowStep
      setStep={setStep}
      title={t('page.createPhotoAlbum.title')}
      subtitle={t('page.createPhotoAlbum.subtitle')}
      showPrev={false}
      disabled={!album.title.trim()}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <label className="font-medium">
            {t('page.createPhotoAlbum.coverPhoto')}
          </label>
          <div className="relative">
            {coverPhotoPreview ? (
              <div className="relative w-[120px] h-[120px]">
                <img
                  src={coverPhotoPreview}
                  alt="Cover preview"
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm hover:bg-red-600"
                >
                  ×
                </button>
              </div>
            ) : (
              <label className="w-[120px] h-[120px] border-2 border-dashed border-primary rounded-lg flex items-center justify-center cursor-pointer hover:border-opacity-80 hover:bg-primary-300 hover:bg-opacity-10">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <div className="text-primary text-2xl">+</div>
              </label>
            )}
          </div>
        </div>

        <Input
          id="album-title"
          label={t('general.title')}
          type="text"
          placeholder={t('page.createPhotoAlbum.titlePlaceholder')}
          value={album.title}
          onChange={(e) => setAlbum({ ...album, title: e.target.value })}
          required
        />

        <p className="text-sm text-gray-600">
          {t('page.createPhotoAlbum.generatedId')}: <code className="text-primary-900">{COMMUNITY_URL}/album/{formatTitleToId(album.title)}</code>
        </p>
      </div>
    </StepFlowStep>
  );
}

function AlbumDetailsView({ setStep, album, setAlbum }: ViewProps) {
  const { t } = useTranslation();

  const handleSubmit = () => {
    setStep(1);
  };

  return (
    <StepFlowStep
      setStep={setStep}
      title={t('page.createPhotoAlbum.title')}
      subtitle={t('page.createPhotoAlbum.subtitle')}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col gap-6">
        <Input
          id="album-description"
          label={t('general.description')}
          type="textarea"
          placeholder={t('page.createPhotoAlbum.descriptionPlaceholder')}
          value={album.description}
          onChange={(e) => setAlbum({ ...album, description: e.target.value })}
          className='h-[100px]'
        />
      </div>
    </StepFlowStep>
  );
}

function UploadSettingsView({ setStep, album, setAlbum }: ViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    setIsCreating(true);
    
    try {
      const finalAlbum = { ...album, id: album.id || formatTitleToId(album.title) };

      await photoAlbumService.createPhotoAlbum(finalAlbum);
      
      toast.success(t('page.createPhotoAlbum.created'));
      navigate('/albums');
    } catch (error) {
      console.error('Error creating album:', error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <StepFlowStep
      setStep={setStep}
      title={t('page.createPhotoAlbum.title')}
      subtitle={t('page.createPhotoAlbum.subtitle')}
      showNext={false}
      onSubmit={handleCreate}
      disabled={isCreating}
      submitText={isCreating ? t('page.createPhotoAlbum.creating') : undefined}
    >
      <div className="flex flex-col gap-1 mb-16">
        <label className="font-medium">
          {t('page.createPhotoAlbum.uploadSettings')}
        </label>
        <Slider
          items={[
            { label: t('component.modal.editAlbum.uploadSettingOpen'), value: true },
            { label: t('component.modal.editAlbum.uploadSettingRestricted'), value: false }
          ]}
          value={album.openToUpload}
          onChange={(value: boolean) => setAlbum({ ...album, openToUpload: value })}
        />
        <p className="text-sm text-gray-600 mt-4">
          {t('page.createPhotoAlbum.uploadSettingsDescription')}
        </p>
      </div>
    </StepFlowStep>
  );
}
